import React, { useEffect } from 'react'
import { makeStyles } from '@material-ui/core'
import { AgGridReact } from '@ag-grid-community/react'
import { AllModules } from '@ag-grid-enterprise/all-modules'
import '@ag-grid-enterprise/all-modules/dist/styles/ag-grid.css'
import '@ag-grid-enterprise/all-modules/dist/styles/ag-theme-material.css'
import './index.css'
import PropTypes from 'prop-types'
import clsx from 'clsx'

const useStyles = makeStyles(theme => ({
  root: {
    display: 'block'
  },
  subTitle: {
    paddingLeft: theme.spacing(3)
  },
  table: {
    height: '60vh',
    width: '100%',
    display: 'grid'
  }
}))

const propTypes = {
  /** Use the naming standards. */
  id: PropTypes.string.isRequired,
  /** Object containing array of data for columns and rows. Column definition guideline: https://www.ag-grid.com/javascript-grid-column-definitions/ */
  data: PropTypes.any,
  /** String for filtering data in the grid */
  query: PropTypes.string,
  /** Optional callback function used by empty data overlay */
  callback: PropTypes.func,
  /** Options for overriding default grid configuration. Grid API Guidelines: https://www.ag-grid.com/javascript-grid-properties/ */
  gridOptions: PropTypes.object,
  /** Custom component to be shown when there is no data in the grid*/
  EmptyOverlay: PropTypes.any,
  /** Optional callback function to retrieve agGrid's API */
  onReady: PropTypes.func
}

const gridDefaults = {
  defaultColDef: {
    resizable: false,
    sortable: true,
    filter: false
  },
  animateRows: true,
  pagination: true,
  paginationAutoPageSize: true,
  suppressCellSelection: true,
  search: ''
}

/**
 * ## DataGrid provides a variety of options about how to present and perform operations over the underlying data, such as paging, sorting, filtering, grouping, editing, etc
 *
 * The `DataGrid` is based on https://www.ag-grid.com/ we only use properties that has been define by our products.
 *
 * ### Concerns:
 *  - Fixed height and width
 *  - Fixed Grid ID
 *  - Multiple Grid
 *
 */

const DataGrid = props => {
  const {
    id,
    data,
    query,
    callback,
    gridOptions,
    EmptyOverlay,
    onReady,
    ...rest
  } = props

  const classes = useStyles()

  const onGridReady = params => {
    if (onReady) {
      onReady(params)
    }
    gridDefaults.api = params.api
    params.api.sizeColumnsToFit()
    gridDefaults.search &&
      gridDefaults.api &&
      gridDefaults.api.setQuickFilter(gridDefaults.search)
    onResize(params)
  }

  const onFilterChanged = params => {
    params.api.getDisplayedRowCount()
      ? params.api.hideOverlay()
      : params.api.showNoRowsOverlay()
  }

  const onResize = params => {
    const container = document.querySelector('[data-cid="DataGrid-container"]')
    container &&
      container.addEventListener('resize', function() {
        params.api.sizeColumnsToFit()
      })
  }

  const onFirstDataRendered = params => {
    params.columnApi.autoSizeColumns(params.columnApi.getAllDisplayedColumns())
    params.api.sizeColumnsToFit()
  }

  const gridData = {
    columnDefs: data.columns,
    rowData: data.rows
  }

  const gridOverlay = EmptyOverlay
    ? {
        noRowsOverlayComponent: 'customNoRowsOverlay',
        noRowsOverlayComponentParams: {
          query: query,
          callback: callback
        },
        frameworkComponents: {
          customNoRowsOverlay: EmptyOverlay
        }
      }
    : {}

  useEffect(() => {
    gridDefaults.search = query
    gridDefaults.api && gridDefaults.api.setQuickFilter(query)
  }, [query])

  const options = {
    ...gridDefaults,
    ...gridData,
    ...gridOverlay,
    ...gridOptions,
    ...rest
  }

  return (
    <div data-cid='DataGrid-container' className={classes.root}>
      <div
        id={id}
        data-cid='DataGrid-root'
        className={clsx('ag-theme-material', classes.table)}
      >
        <AgGridReact
          onGridReady={onGridReady}
          onFilterChanged={onFilterChanged}
          onFirstDataRendered={onFirstDataRendered}
          modules={AllModules}
          {...options}
        />
      </div>
    </div>
  )
}

DataGrid.propTypes = propTypes

export default DataGrid
